/**
 * Visibility Windows Configuration
 *
 * Date-bound list views hide their oldest entries. Keeping those windows in one
 * place makes the behaviour explicit and unit-testable.
 *
 * Feature 34: an etappe (catechesis item) stays visible for 4 weeks after its
 * date, so catechists can still consult recent etappes instead of losing them the
 * day after they take place. The conviventie window is unrelated and still lives
 * in `ledenUploadTemplate.js` (`VISIBILITY_GRACE_DAYS`).
 */

/** Number of weeks an etappe stays visible after its `datum`. */
export const ETAPPE_VISIBILITY_WEEKS = 4;

const DAYS_PER_WEEK = 7;

/**
 * Formats a date as a zero-padded `YYYY-MM-DD` string from its local date parts.
 *
 * @param {Date} date
 * @returns {string}
 */
const formatLocalYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Earliest `datum` that is still visible: `weeks` before `today`, inclusive.
 *
 * Local date parts are used on purpose: `toISOString()` is UTC-based, which would
 * hide a same-day etappe during the first hours of the day in CET.
 *
 * @param {Date} [today]
 * @param {number} [weeks]
 * @returns {string} `YYYY-MM-DD`
 */
export const getEtappeVisibilityCutoff = (today = new Date(), weeks = ETAPPE_VISIBILITY_WEEKS) => {
  const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  cutoff.setDate(cutoff.getDate() - weeks * DAYS_PER_WEEK);
  return formatLocalYmd(cutoff);
};

/**
 * Whether an etappe with the given `datum` is still visible.
 *
 * Zero-padded `YYYY-MM-DD` values compare chronologically as strings, which is
 * how the rest of the app compares dates.
 *
 * @param {string} datum
 * @param {Date} [today]
 * @returns {boolean}
 */
export const isEtappeVisible = (datum, today = new Date()) => {
  const value = String(datum || '').trim();
  if (!value) return false;

  return value >= getEtappeVisibilityCutoff(today);
};
